import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    TablePagination,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import useTitle from "hooks/useTitle";
import { getMcaFilingStatus } from "api/services/mca/mca";

interface FilingItem {
    dmsid: number;
    eform: string;
    eventdate: string;
    srn: string;
}

const McaFilingStatus = () => {
    useTitle("ATOM Pro | MCA ");

    const [cin, setCin] = useState("");
    const [searchCin, setSearchCin] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
    } = useQuery(
        ["get-mca-filing-status", { cin: searchCin }],
        getMcaFilingStatus,
        {
            enabled: !!searchCin,
            retry: 1,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
        }
    );


    const rows: FilingItem[] = useMemo(() => {
        if (!searchCin) return [];
        if (!data?.success) return [];

        const apiData: FilingItem[] = data?.data?.data || [];

        const unique = Array.from(
            new Map(apiData.map((item) => [item.srn, item])).values()
        );

        return unique.sort((a, b) => {
            const dateA = new Date(a.eventdate.split("-").reverse().join("-"));
            const dateB = new Date(b.eventdate.split("-").reverse().join("-"));
            return dateB.getTime() - dateA.getTime();
        });
    }, [data]);

    const paginatedRows = useMemo(() => {
        return rows.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        );
    }, [rows, page, rowsPerPage]);

    const handleSearch = () => {
        const trimmed = cin.trim();
        if (!trimmed) return; 

        setSearchCin(trimmed);
        setPage(0);
    };



    return (
        <Box
            sx={{
                minHeight: "100vh",
                px: { xs: 2, md: 4 },
                py: 4,
                background: "linear-gradient(135deg, #eef2ff, #f8fafc)",
            }}
        >
            <Box textAlign="center" mb={5}>
                <Typography variant="h5" fontWeight={700}>
                    MCA Filing Status
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Search company filing records using CIN number
                </Typography>
            </Box>

            <Paper
                sx={{
                    p: 4,
                    mb: 5,
                    borderRadius: "20px",
                    background: "rgba(255,255,255,0.25)",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    border: "1px solid rgba(255,255,255,0.4)",
                    boxShadow: `
          0 12px 40px rgba(0,0,0,0.08),
          inset 0 1px 2px rgba(255,255,255,0.6)
        `,
                }}
            >
                <Box
                    display="flex"
                    gap={2}
                    flexDirection={{ xs: "column", sm: "row" }}
                    alignItems="center"
                >
                    <TextField
                        fullWidth
                        label="Enter CIN Number"
                        value={cin}
                        onChange={(e) =>
                            setCin(e.target.value.toUpperCase())
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSearch();
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "14px",
                                background: "rgba(255,255,255,0.6)",
                                backdropFilter: "blur(8px)",
                            },
                        }}
                    />

                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleSearch}
                        disabled={!cin.trim()}
                        sx={{
                            px: 4,
                            height: "56px",
                            borderRadius: "14px",
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#fff",

                            background: "rgba(0,0,0,0.6)",
                            backdropFilter: "blur(12px)",
                            border: "1px solid rgba(255,255,255,0.25)",

                            boxShadow: `
              0 6px 20px rgba(0,0,0,0.4),
              inset 0 1px 1px rgba(255,255,255,0.2)
            `,

                            "&:hover": {
                                background: "rgba(37, 99, 235, 0.75)",
                                transform: "translateY(-2px)",
                                boxShadow: `
                0 12px 30px rgba(37,99,235,0.6),
                inset 0 1px 2px rgba(255,255,255,0.5)
              `,
                            },
                        }}
                    >
                        Search
                    </Button>
                </Box>
            </Paper>

            {isLoading || isFetching ? (
                <Box textAlign="center" mt={5}>
                    <CircularProgress />
                </Box>
            ) : isError || data?.success === false ? (
                <Alert
                    severity="error"
                    sx={{ borderRadius: "14px", backdropFilter: "blur(10px)" }}
                >
                    {
                        data?.message ||
                        "Failed to fetch MCA data. Try again later."}
                </Alert>
            ) : searchCin && rows.length === 0 && !isError && !isLoading && !isFetching && !data?.success === false  ? (
                <Alert
                    severity="info"
                    sx={{
                        borderRadius: "14px",
                        backdropFilter: "blur(10px)",
                    }}
                >
                    No filing records found for CIN: <b>{searchCin}</b>
                </Alert>
            ) : rows.length > 0 ? (
                <Paper
                    sx={{
                        borderRadius: "22px",
                        overflow: "hidden",
                        background: "rgba(255,255,255,0.25)",
                        backdropFilter: "blur(18px)",
                        WebkitBackdropFilter: "blur(18px)",
                        border: "1px solid rgba(255,255,255,0.4)",
                        boxShadow: `
        0 15px 45px rgba(0,0,0,0.08),
        inset 0 1px 2px rgba(255,255,255,0.6)
      `,
                    }}
                >
                    <TableContainer sx={{ maxHeight: 500 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {["SRN", "E-Form Name", "Event Date"].map((header, i) => (
                                        <TableCell
                                            key={i}
                                            sx={{
                                                fontWeight: 700,
                                                background: "rgba(255,255,255,0.6)",
                                                backdropFilter: "blur(6px)",
                                            }}
                                        >
                                            {header}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedRows.map((row) => (
                                    <TableRow
                                        key={`${row.srn}-${row.eventdate}`}
                                        hover
                                        sx={{
                                            transition: "0.3s",
                                            "&:hover": {
                                                background: "rgba(37,99,235,0.08)",
                                            },
                                        }}
                                    >
                                        <TableCell>{row.srn}</TableCell>
                                        <TableCell>{row.eform}</TableCell>
                                        <TableCell>{row.eventdate}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={rows.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setPage(0);
                        }}
                        sx={{
                            backdropFilter: "blur(10px)",
                            borderTop: "1px solid rgba(255,255,255,0.4)",
                        }}
                    />
                </Paper>
            ) : null}

        </Box>
    );


};

export default McaFilingStatus;
